import React, { useMemo, useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import MapGL, { Marker as MapMarker, Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

import { Marker } from './Marker';
import { InfoWindow } from './InfoWindow';
import { GroupedMarker } from './GroupedMarker';
import { StartPoint } from './StartPoint';
import { EndPoint } from './EndPoint';
import { getLatLng, getOrderGeo, RouteElementType, isHiddenFromMap } from './../../utils/utils';
import { getDaysUntilPickupAt } from './../../utils/getTransitCounterLevel';
import {
  PLANNING_MAP_MODE,
  hasPlanningTier,
  getPlanningTierColor,
  getPlanningMarkerStyle,
  isVisibleByCutoff,
  buildPlanningPopulation,
  buildTierVisibilityModel,
  buildVisibilitySteps,
  VISIBILITY_MAX_STEPS_PER_TIER,
  cutoffForStep,
  tierStepTicks,
  withSpreadLabelPercents,
  indexForTierStep,
  shouldAutoAdvance,
  visibilitySliderWidth,
  anchorForStepIndex,
  stepIndexForAnchor,
  loadVisibilityAnchor,
  PLANNING_VISIBILITY_ANCHOR_KEY,
  getGroupColor,
  getGroupSizeBoost,
  getPlanningRequestCount,
  PLANNING_MARKER_RADII,
  PLANNING_PULSE_CONFIG,
  TRANSIT_FILL_COLOR,
} from './../../utils/planningTier';
import { PlanningVisibilitySlider } from './PlanningVisibilitySlider';
import { PlanningModeToggle } from './PlanningModeToggle';

const PLANNING_MODE_STORAGE_KEY = 'route-map-planning-mode';
const PLANNING_MARK_ATTEMPTS_KEY = 'route-map-planning-mark-attempts';
const MAX_TRANSIT_VISIBILITY_DAYS = 5;


/**
 * Determine marker color for an order based on its status/pickupAt date.
 * In planning mode, warehouse orders with a calculated tier use the tier color.
 */
function getOrderColor(order, planningMode = PLANNING_MAP_MODE.LEGACY) {
  const WAREHOUSE = '#9370DB';
  const DEFAULT = '#5B5B5B';

  // Planning mode: any warehouse-phase order with a calculated tier (status 3 OR
  // return 13) is tinted with its tier color, consistent with the counter/slider.
  if (planningMode === PLANNING_MAP_MODE.PLANNING && hasPlanningTier(order)) {
    return getPlanningTierColor(order, WAREHOUSE);
  }

  if (order.statusId === 3) {
    return WAREHOUSE;
  }

  const daysUntilPickupAt = getDaysUntilPickupAt(order);

  if (daysUntilPickupAt === null) return DEFAULT;

  if (planningMode === PLANNING_MAP_MODE.PLANNING) {
    return daysUntilPickupAt >= 0 ? TRANSIT_FILL_COLOR : DEFAULT;
  }

  if (daysUntilPickupAt > 3) return '#8FBCEE';
  if (daysUntilPickupAt > 1) return '#5A8DEE';
  if (daysUntilPickupAt === 1) return '#3A6DD8';
  if (daysUntilPickupAt === 0) return '#2555B0';
  return DEFAULT;
}

function getOrderPriority(order, planningMode = PLANNING_MAP_MODE.LEGACY) {
  if (!order) return 0;

  const selectedBonus = order.selected ? 10000 : 0;
  const urgentBonus = order.isUrgent ? 50 : 0;

  if (planningMode === PLANNING_MAP_MODE.PLANNING && hasPlanningTier(order)) {
    const daysLeft = Number(order.warehouseDaysLeft ?? 0);
    return selectedBonus + order.warehouseTier * 1000 + urgentBonus + Math.max(0, 200 - daysLeft);
  }

  const daysUntilPickupAt = getDaysUntilPickupAt(order);
  if (daysUntilPickupAt !== null) {
    return selectedBonus + 100 + urgentBonus + Math.max(0, 20 - daysUntilPickupAt);
  }

  return selectedBonus + urgentBonus;
}

function isTransitVisibleByDays(order, transitDays, isSelected = false) {
  if (isSelected) return true;
  const daysUntilPickupAt = getDaysUntilPickupAt(order);
  if (daysUntilPickupAt === null) return true;
  if (daysUntilPickupAt < 0) return false;
  if (transitDays <= 0) return false;
  return daysUntilPickupAt < transitDays;
}

const defaultCenter = [51.738183, 19.274329];
const DEFAULT_ZOOM = 6;

const MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

const ROUTE_LINE_PAINT = {
  'line-color': '#2563eb',
  'line-width': 5,
  'line-opacity': 0.75,
};

const POINT_LAYER_IDS = ['all-points-layer'];
// Dynamic +N labels are rendered by a text-only symbol layer over the normal
// tier-coloured marker. Transit pre-advice never replaces the tier colour.
const TRANSIT_COUNTER_SIZE = 1;
const TRANSIT_COUNTER_PIXEL_RATIO = 1;
const TRANSIT_COUNTER_ICON_PATTERN = /^transit-counter-(\d)(-urgent)?$/;

function getAvailablePointLayerIds(map) {
  return POINT_LAYER_IDS.filter((layerId) => map.getLayer(layerId));
}

function createTransitCounterImage(_level, _isUrgent = false) {
  const canvas = document.createElement('canvas');
  canvas.width = TRANSIT_COUNTER_SIZE;
  canvas.height = TRANSIT_COUNTER_SIZE;

  return canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
}

function registerTransitCounterImages(_map) {
  // Transit +N labels are now text-only symbols over normal tier-coloured points.
}

function ensureTransitCounterImage(map, iconId) {
  const match = iconId?.match(TRANSIT_COUNTER_ICON_PATTERN);

  if (!match || map.hasImage(iconId)) {
    return;
  }

  const level = Number(match[1]);
  const isUrgent = Boolean(match[2]);

  map.addImage(iconId, createTransitCounterImage(level, isUrgent), { pixelRatio: TRANSIT_COUNTER_PIXEL_RATIO });
}

function setMapCursor(map, cursor) {
  const canvas = map.getCanvas();
  const canvasContainer = map.getCanvasContainer();

  if (canvas) {
    canvas.style.cursor = cursor;
  }

  if (canvasContainer) {
    canvasContainer.style.cursor = cursor;
    canvasContainer.classList.toggle('maplibregl-track-pointer', cursor === 'pointer');
  }
}

function stopMarkerMapEvent(event) {
  event.preventDefault();
  event.stopPropagation();
  event.nativeEvent?.stopImmediatePropagation?.();
}

export const Map = ({
  routeId,
  routeOrders = [],
  onSelectOrder = () => {},
  onUnselectOrder = () => {},
  isLoading = false,
  points = [],
  startPointLocation = null,
  endPointLocation = null,
  onMapLoad = () => {},
  onGoogleMapChange = () => {},
  routePolylines = [],
  snappedPositions = {},
  showAllMarkers = false,
  roadSnappedPositions = {},
  fitRouteBoundsOnRender = false,
  hasReturnPicking = false,
  children,
}) => {
  const [infoOrder, setInfoOrder] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [viewCounter, setViewCounter] = useState(0);
  const [planningMode, setPlanningMode] = useState(() => {
    try {
      // Planning view is ON by default; only an explicit 'legacy' opt-out disables it.
      return window.localStorage.getItem(PLANNING_MODE_STORAGE_KEY) === PLANNING_MAP_MODE.LEGACY
        ? PLANNING_MAP_MODE.LEGACY
        : PLANNING_MAP_MODE.PLANNING;
    } catch (e) {
      return PLANNING_MAP_MODE.PLANNING;
    }
  });
  // Discrete step index into visibilitySteps. Persisted as a semantic anchor
  // (fraction 0..1) so the position survives route/data changes gracefully.
  // Initialised from the stored anchor once visibilitySteps is known (see effect).
  const [visibilityStepIndex, setVisibilityStepIndex] = useState(0);
  // Semantic anchor persisted in localStorage. Loaded once at mount; re-derived
  // and saved whenever the user moves the slider or clicks a tier.
  const visibilityAnchorRef = useRef(loadVisibilityAnchor());
  // Transient "peek at everything" override toggled with Space (not persisted).
  const [showAllOverride, setShowAllOverride] = useState(false);
  // Optional count of planning requests sent to the customer. The internal
  // failed-attempt count remains a separate backend signal for deadline/tier rules.
  const [markAttempts, setMarkAttempts] = useState(() => {
    try {
      return window.localStorage.getItem(PLANNING_MARK_ATTEMPTS_KEY) === '1';
    } catch (e) {
      return false;
    }
  });

  const handleMarkAttemptsChange = useCallback((next) => {
    setMarkAttempts(next);
    try { window.localStorage.setItem(PLANNING_MARK_ATTEMPTS_KEY, next ? '1' : '0'); } catch (e) { /* ignore */ }
  }, []);

  // Do not persist the selected planning panel screen: every page refresh starts
  // from the Tiers slider. The transit/awizacja slider also always starts from 0.
  const [planningPanelScreen, setPlanningPanelScreen] = useState('deadlines');
  const [transitVisibilityDays, setTransitVisibilityDays] = useState(0);

  const handlePlanningPanelScreenChange = useCallback((screen) => {
    setPlanningPanelScreen(screen);

    if (screen === 'transit') {
      setMarkAttempts(false);
      try { window.localStorage.setItem(PLANNING_MARK_ATTEMPTS_KEY, '0'); } catch (e) { /* ignore */ }
    }
  }, []);

  const handleTransitVisibilityDaysChange = useCallback((next) => {
    setTransitVisibilityDays(Math.max(0, Math.min(MAX_TRANSIT_VISIBILITY_DAYS, next)));
  }, []);

  const handlePlanningModeChange = useCallback((mode) => {
    setPlanningMode(mode);
    try { window.localStorage.setItem(PLANNING_MODE_STORAGE_KEY, mode); } catch (e) { /* ignore */ }
  }, []);

  // The discrete step array derived from the tier visibility model. Rebuilt once
  // per data change (new route / new points); the slider index is an O(1) lookup.
  // Placed here (before handleStepIndexChange) so the callback can close over it.
  // Ref mirror of the current steps so handleStepIndexChange (a stable callback)
  // can always read the latest steps without being re-created. The steps array
  // itself is derived directly from the model via useMemo below (single source
  // of truth — no state copy, so render never lags a frame behind the data).
  const visibilityStepsRef = useRef([{ cutoff: Number.NEGATIVE_INFINITY, tier: null }]);

  // Unified setter: clamps index, cancels peek, persists semantic anchor.
  const handleStepIndexChange = useCallback((nextIndex) => {
    const steps = visibilityStepsRef.current;
    const clamped = Math.max(0, Math.min(steps.length - 1, Math.round(nextIndex)));
    setVisibilityStepIndex(clamped);
    setShowAllOverride(false);
    const anchor = anchorForStepIndex(steps, clamped);
    visibilityAnchorRef.current = anchor;
    try { window.localStorage.setItem(PLANNING_VISIBILITY_ANCHOR_KEY, String(anchor)); } catch (e) { /* ignore */ }
  }, []);

  const isPlanningMode = planningMode === PLANNING_MAP_MODE.PLANNING;
  const [isMapReady, setIsMapReady] = useState(false);
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const overlayPortalRef = useRef(null);
  const showAllMarkersRef = useRef(showAllMarkers);
  const pointsByIdRef = useRef({});
  const overlayMetricsRef = useRef({ left: 0, top: 0, width: 0, height: 0 });
  const hasAutoFittedRef = useRef(false);
  const pulseRafRef = useRef(null);
  const pulseStartedRef = useRef(false);

  const handleGroupToggle = useCallback((coordKey, isExpanded) => {
    setExpandedGroups(prev => ({ ...prev, [coordKey]: isExpanded }));
  }, []);

  const getOverlayPadding = useCallback(() => {
    const metrics = overlayMetricsRef.current || {};
    const hasOverlay = metrics.width > 0;

    return {
      top: Math.max(24, (metrics.top || 0) + 12),
      right: 24,
      bottom: 24,
      left: hasOverlay ? Math.round(metrics.left + metrics.width + 24) : 24,
    };
  }, []);

  const fitRouteBounds = useCallback(() => {
    if (!mapRef.current) return false;

    const allPoints = [];

    if (routePolylines && routePolylines.length > 0) {
      routePolylines.forEach((p) => {
        if (p.positions) p.positions.forEach((pos) => allPoints.push(pos));
      });
    }

    if (routeOrders && routeOrders.length > 0) {
      routeOrders.forEach((ro) => {
        if (ro.order && getOrderGeo(ro.order)) {
          try {
            const { lat, lng } = getLatLng(ro.order);
            allPoints.push([lat, lng]);
          } catch (e) { /* skip */ }
        }
      });
    }

    if (startPointLocation && startPointLocation.lat) {
      allPoints.push([startPointLocation.lat, startPointLocation.lng]);
    }
    if (endPointLocation && endPointLocation.lat) {
      allPoints.push([endPointLocation.lat, endPointLocation.lng]);
    }

    if (allPoints.length >= 2) {
      let minLat = Infinity;
      let maxLat = -Infinity;
      let minLng = Infinity;
      let maxLng = -Infinity;

      allPoints.forEach(([lat, lng]) => {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      });

      mapRef.current.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: getOverlayPadding(),
          duration: 1200,
          maxZoom: 13,
          essential: true,
        }
      );

      return true;
    }

    return false;
  }, [routePolylines, routeOrders, startPointLocation, endPointLocation, getOverlayPadding]);

  useLayoutEffect(() => {
    const overlayEl = overlayPortalRef.current;
    const containerEl = containerRef.current;

    if (!overlayEl || !containerEl) return;

    const updateMetrics = () => {
      const overlayRect = overlayEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();

      overlayMetricsRef.current = {
        left: Math.max(0, overlayRect.left - containerRect.left),
        top: Math.max(0, overlayRect.top - containerRect.top),
        width: overlayRect.width,
        height: overlayRect.height,
      };
    };

    updateMetrics();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        updateMetrics();
      });
      resizeObserver.observe(overlayEl);

      return () => resizeObserver.disconnect();
    }

    window.addEventListener('resize', updateMetrics);

    return () => window.removeEventListener('resize', updateMetrics);
  }, [children]);

  // Fire fitBounds exactly once per mount, the first time the polyline has
  // enough points to compute a meaningful camera. Later polyline changes
  // (show route / optimize via Google / drag-and-drop) must NOT bounce
  // the planner's camera back to the full-route view.
  useEffect(() => {
    if (!fitRouteBoundsOnRender) return;
    if (!isMapReady) return;
    if (hasAutoFittedRef.current) return;
    const didFit = fitRouteBounds();
    if (didFit) {
      hasAutoFittedRef.current = true;
    }
  }, [fitRouteBoundsOnRender, routePolylines, fitRouteBounds, isMapReady]);

  const handleMoveEnd = useCallback(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const bounds = map.getBounds();
    onGoogleMapChange({
      zoom: map.getZoom(),
      bounds: {
        nw: { lat: bounds.getNorth(), lng: bounds.getWest() },
        se: { lat: bounds.getSouth(), lng: bounds.getEast() },
      },
    });
    setViewCounter(c => c + 1);
  }, [onGoogleMapChange]);

  const pointsById = useMemo(() => {
    const map = {};

    if (points) {
      for (const point of points) {
        const order = point?.properties?.order || point?.order;

        if (order) {
          map[order.id] = order;
        }
      }
    }

    return map;
  }, [points]);

  // Keep native MapLibre handlers in sync without re-registering them on every render.
  showAllMarkersRef.current = showAllMarkers;
  pointsByIdRef.current = pointsById;

  const openInfoWindowForRenderedPoint = useCallback((point, originalEvent) => {
    const map = mapRef.current;

    if (!map || !showAllMarkersRef.current) return false;
    if (originalEvent?.target?.closest?.('.info-window')) return false;
    if (originalEvent?.target?.closest?.('.maplibregl-marker')) return false;

    try {
      const availableLayers = getAvailablePointLayerIds(map);
      if (availableLayers.length === 0) return false;

      const features = map.queryRenderedFeatures(point, { layers: availableLayers });
      if (features.length === 0) return false;

      const order = pointsByIdRef.current[features[0].properties.id];
      if (!order) return false;

      originalEvent?.preventDefault?.();
      setInfoOrder(order);
      return true;
    } catch (ex) {
      return false;
    }
  }, []);

  const handleMapClick = useCallback((evt) => {
    if (evt?.originalEvent?.button && evt.originalEvent.button !== 0) {
      return;
    }

    setInfoOrder(null);

    // Handle click on native WebGL layer
    if (showAllMarkers && evt.features && evt.features.length > 0) {
      const clickedId = evt.features[0].properties.id;
      const order = pointsByIdRef.current[clickedId];
      if (order) onSelectOrder(order);
    }
  }, [showAllMarkers, onSelectOrder]);

  const handleMapContextMenuCapture = useCallback((event) => {
    const map = mapRef.current;

    if (!map) return;

    const rect = map.getCanvas().getBoundingClientRect();
    const point = [event.clientX - rect.left, event.clientY - rect.top];

    openInfoWindowForRenderedPoint(point, event.nativeEvent ?? event);
  }, [openInfoWindowForRenderedPoint]);

  const handleMapMouseMoveCapture = useCallback((event) => {
    const map = mapRef.current;

    if (!map || !showAllMarkersRef.current) {
      if (map) {
        setMapCursor(map, '');
      }
      return;
    }

    const rect = map.getCanvas().getBoundingClientRect();
    const point = [event.clientX - rect.left, event.clientY - rect.top];

    try {
      const availableLayers = getAvailablePointLayerIds(map);

      if (availableLayers.length === 0) {
        setMapCursor(map, '');
        return;
      }

      const features = map.queryRenderedFeatures(point, { layers: availableLayers });
      setMapCursor(map, features.length > 0 ? 'pointer' : '');
    } catch (ex) {
      setMapCursor(map, '');
    }
  }, []);

  const handleMapMouseLeave = useCallback(() => {
    if (mapRef.current) {
      setMapCursor(mapRef.current, '');
    }
  }, []);

  const handleMapLoad = useCallback((evt) => {
    const map = evt.target;
    mapRef.current = map;
    setIsMapReady(true);
    registerTransitCounterImages(map);

    // Kill the remaining rotation gestures while KEEPING pinch-zoom and
    // keyboard pan/zoom: two-finger twist and Shift+arrows (ticket 869efmumy).
    // Mouse drag-rotate is disabled via the dragRotate prop below.
    map.touchZoomRotate.disableRotation();
    map.keyboard.disableRotation();

    // Wheel zoom uses MapLibre's native ScrollZoomHandler. The handler
    // already does accumulator + RAF interpolation with a smooth-out
    // bezier; the stepped-zoom bug on Chromium comes from a single
    // timing issue in renderFrame() (upstream PR #5154). We ship that
    // fix via patch-package → patches/maplibre-gl+4.7.1.patch.
    // See: https://github.com/maplibre/maplibre-gl-js/pull/5154

    map.on('styleimagemissing', ({ id }) => {
      ensureTransitCounterImage(map, id);
    });

    map.on('styledata', () => {
      registerTransitCounterImages(map);
    });

    // Fix half-gray rendering: force resize after layout settles
    setTimeout(() => map.resize(), 100);

    map.on('contextmenu', (event) => {
      openInfoWindowForRenderedPoint(event.point, event.originalEvent);
    });

    // Expose a proxy API compatible with RouteMap.js expectations.
    // RouteMap.js calls panTo([lat, lng]) (Leaflet convention), MapLibre expects [lng, lat].
    onMapLoad({
      panTo: ([lat, lng]) => map.panTo([lng, lat], { duration: 900, essential: true }),
      flyTo: (opts) => map.flyTo({ duration: 900, essential: true, ...opts }),
      getBounds: () => map.getBounds(),
      getZoom: () => map.getZoom(),
    });
  }, [onMapLoad, openInfoWindowForRenderedPoint]);


  const BASE_RADIUS = 120;

  const handleFullscreen = useCallback(() => {
    const mapContainer = containerRef.current;
    if (!mapContainer) return;
    if (!document.fullscreenElement) {
      mapContainer.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleZoomIn = useCallback(() => {
    if (mapRef.current) mapRef.current.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) mapRef.current.zoomOut();
  }, []);

  const groupedMarkers = useMemo(() => {
    const groups = {};

    // Collect all visible points (when "show all" is active)
    if (showAllMarkers && points && points.length > 0) {
      points.forEach((point) => {
        const order = point?.properties?.order || point?.order;
        if (order) {
          try {
            const { lat, lng } = getLatLng(order);
            const coordKey = `${lat.toFixed(3)},${lng.toFixed(3)}`;

            if (!groups[coordKey]) {
              groups[coordKey] = {
                coordinates: [lng, lat],
                markers: [],
                isExpanded: false,
              };
            }

            groups[coordKey].markers.push({
              id: order.id || `point-${Math.random()}`,
              geometry: { coordinates: [lng, lat] },
              properties: { order },
            });
          } catch (error) {
            console.warn('Error processing point:', point, error);
          }
        }
      });
    }

    // Add route orders (selected orders on route)
    if (routeOrders && routeOrders.length > 0) {
      const filteredRouteOrders = routeOrders.filter(
        (routeOrder) => routeOrder.type === RouteElementType.ROUTE_ORDER && !isHiddenFromMap(routeOrder.order)
      );

      filteredRouteOrders.forEach(({ order }) => {
        if (order && getOrderGeo(order)) {
          try {
            const { lat, lng } = getLatLng(order);
            const coordKey = `${lat.toFixed(3)},${lng.toFixed(3)}`;

            if (!groups[coordKey]) {
              groups[coordKey] = {
                coordinates: [lng, lat],
                markers: [],
                isExpanded: false,
              };
            }

            const existingOrder = groups[coordKey].markers.find(
              (marker) => marker.properties.order.id === order.id
            );

            if (!existingOrder) {
              groups[coordKey].markers.push({
                id: order.id || `route-${Math.random()}`,
                geometry: { coordinates: [lng, lat] },
                properties: { order: { ...order, selected: true } },
              });
            }
          } catch (error) {
            console.warn('Error processing route order:', order, error);
          }
        }
      });
    }

    // Only keep groups with more than 1 marker at the same location
    const filteredGroups = {};
    Object.keys(groups).forEach((key) => {
      if (groups[key].markers.length > 1) {
        filteredGroups[key] = groups[key];
      }
    });

    return filteredGroups;
  }, [points, routeOrders, showAllMarkers]);

  // GeoJSON for connector lines between group center and expanded markers (rendered natively by MapLibre)
  const connectorLinesGeoJSON = useMemo(() => {
    if (!mapRef.current) return { type: 'FeatureCollection', features: [] };
    const features = [];
    Object.entries(expandedGroups).forEach(([coordKey, isExpanded]) => {
      if (!isExpanded) return;
      const group = groupedMarkers[coordKey];
      if (!group) return;
      const [cLng, cLat] = group.coordinates;
      const centerPx = mapRef.current.project([cLng, cLat]);
      const radius = BASE_RADIUS + group.markers.length * 4;

      group.markers
        .slice()
        .sort((a, b) => a.properties.order.id - b.properties.order.id)
        .forEach((marker, index) => {
          const angle = (index / group.markers.length) * 2 * Math.PI;
          const adjustedAngle = angle - Math.PI / 2;
          const px = centerPx.x + radius * Math.cos(adjustedAngle);
          const py = centerPx.y + radius * Math.sin(adjustedAngle);
          const lngLat = mapRef.current.unproject([px, py]);
          features.push({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [[cLng, cLat], [lngLat.lng, lngLat.lat]],
            },
            properties: {},
          });
        });
    });
    return { type: 'FeatureCollection', features };
  }, [expandedGroups, groupedMarkers, viewCounter]);

  const filteredRouteOrders = useMemo(
    () => routeOrders.filter((ro) => ro.type === RouteElementType.ROUTE_ORDER),
    [routeOrders]
  );

  // Set of selected order IDs to prevent double-rendering
  const selectedOrderIds = useMemo(
    () => new Set(filteredRouteOrders.map(ro => ro.order?.id).filter(Boolean)),
    [filteredRouteOrders]
  );

  // Full planning population = available map points + already-selected route
  // orders. In edit-route mode a clicked point moves from `points` to
  // `routeOrders`; if the slider model used `points` only, the step list changed
  // immediately after the first click and could reveal the next portion too
  // early. Keeping selected orders in this population makes the cohort shape
  // stable until the user selects ALL currently visible unselected points
  // (subtask 869dxzp71). Deduplicate by id because a selected order can appear in
  // both inputs during a transient render.
  const planningPopulation = useMemo(
    () => buildPlanningPopulation(points || [], filteredRouteOrders),
    [points, filteredRouteOrders],
  );

  // Tier-stepped visibility model over the FULL route population — built ONCE per
  // data change. The slider then does an O(1) cutoff lookup into it (no per-tick
  // sort). Selected (on-route) orders are intentionally KEPT in the model so the
  // step list stays stable while the user picks points: they are already always
  // visible via the isVisibleByCutoff bypass, and excluding them made selecting a
  // cohort's last point silently drop a step and reveal the next cohort out of
  // turn (subtask 869dxzp71). Cohort shape now depends only on the data, not on
  // selection; slider advancement is handled explicitly by the auto-advance
  // effect below.
  const tierVisibilityModel = useMemo(
    () => buildTierVisibilityModel(planningPopulation),
    [planningPopulation]
  );

  // Flat ordered step array: one entry per distinct score cohort + show-all.
  // Single source of truth: rebuilt directly from the model. Render always sees
  // the current steps (no state copy / no one-frame "show all" flash on route
  // change). The ref mirror is updated synchronously for stable callbacks.
  const visibilitySteps = useMemo(
    () => buildVisibilitySteps(tierVisibilityModel, {
      maxStepsPerTier: VISIBILITY_MAX_STEPS_PER_TIER,
    }),
    [tierVisibilityModel]
  );
  visibilityStepsRef.current = visibilitySteps;

  // When the data (and thus steps) change, re-clamp the step index from the
  // persisted semantic anchor so the slider position stays meaningful across
  // different route populations. Only the index is state; steps are derived.
  useEffect(() => {
    setVisibilityStepIndex(stepIndexForAnchor(visibilitySteps, visibilityAnchorRef.current));
  }, [visibilitySteps]);

  // Clickable tier ticks under the slider (one per tier, at last-cohort index).
  // labelPercent is spread apart so captions never overlap even when the
  // least-urgent tier owns many cohorts and pushes the low tiers together on the
  // left (subtask 869dxzmum). percent (true cohort position) is kept for logic.
  const sliderWidth = visibilitySliderWidth(visibilitySteps.length);
  const tierTicks = useMemo(
    () => withSpreadLabelPercents(tierStepTicks(visibilitySteps), sliderWidth),
    [visibilitySteps, sliderWidth],
  );

  // Effective index: Space "peek" override snaps to show-all without persisting.
  const effectiveIndex = showAllOverride
    ? visibilitySteps.length - 1
    : Math.max(0, Math.min(visibilitySteps.length - 1, visibilityStepIndex));

  // O(1) cutoff lookup — isVisibleByCutoff(order, visibilityCutoff) unchanged.
  const visibilityCutoff = cutoffForStep(visibilitySteps, effectiveIndex);

  // Clicking a tier tick jumps to that tier's last-cohort index (whole tier +
  // every more-urgent visible, next tier not yet). Restores focus to map so
  // Space peek keybind fires correctly (fix 869dxzrpe — do NOT remove).
  const handleTierStepClick = useCallback((tier) => {
    const steps = visibilityStepsRef.current;
    handleStepIndexChange(indexForTierStep(steps, tier));
    // Return focus to the map container so the Space "peek" keybind fires
    // instead of re-activating the tier button (which caused a black focus ring).
    containerRef.current?.focus({ preventScroll: true });
  }, [handleStepIndexChange]);

  // Planning visibility predicate: the tier-stepped cutoff applies, and the
  // awizacja screen controls how many pickup-at day buckets stay visible.
  // Selected (on-route) orders are always visible.
  const isPlanningPointVisible = useCallback((order, isSelected) => {
    if (isSelected) return true;
    if (!isTransitVisibleByDays(order, transitVisibilityDays, isSelected)) return false;
    return isVisibleByCutoff(order, visibilityCutoff, isSelected);
  }, [visibilityCutoff, transitVisibilityDays]);

  // Auto-advance (subtask 869dxzp71): once every point currently visible on the
  // map has been selected, step the slider forward by one so the next cohort
  // appears — but NOT after selecting just one of several visible points. The
  // decision lives in the pure shouldAutoAdvance helper; here we only bind the
  // live predicates. Runs on selection changes; skipped during Space peek.
  useEffect(() => {
    if (!isPlanningMode || showAllOverride) return;
    const isSelected = (o) => o != null && selectedOrderIds.has(o.id);
    const isVisible = (o) => isPlanningPointVisible(o, isSelected(o));
    // Step-independent transit/awizacja filter, so a cohort hidden by it never
    // triggers a cascade that reveals points the planner cannot act on.
    const passesTransitFilter = (o) =>
      isTransitVisibleByDays(o, transitVisibilityDays, isSelected(o));
    if (shouldAutoAdvance({
      steps: visibilitySteps,
      currentIndex: visibilityStepIndex,
      points: planningPopulation,
      isVisible,
      isSelected,
      passesTransitFilter,
    })) {
      handleStepIndexChange(visibilityStepIndex + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderIds, visibilitySteps, visibilityStepIndex, planningPopulation, isPlanningMode, showAllOverride, isPlanningPointVisible, transitVisibilityDays]);

  // Split ungrouped points into WebGL circles and badge-only overlays.
  const { plainPointsGeoJSON, badgePoints } = useMemo(() => {
    if (!showAllMarkers || !points || points.length === 0) {
      return {
        plainPointsGeoJSON: { type: 'FeatureCollection', features: [] },
        badgePoints: [],
      };
    }
    const plainFeatures = [];
    const badges = [];

    for (const point of points) {
      const order = point?.properties?.order || point?.order;
      if (!order) continue;
      if (selectedOrderIds.has(order.id)) continue;
      // Planning visibility: slider percentage or per-tier filter (see predicate).
      if (isPlanningMode && !isPlanningPointVisible(order, selectedOrderIds.has(order.id))) {
        continue;
      }
      try {
        const { lat, lng } = getLatLng(order);
        const coordKey = `${lat.toFixed(3)},${lng.toFixed(3)}`;
        if (groupedMarkers && groupedMarkers[coordKey]) continue;

        const isReturn = order.type === 'return';
        const isExchange = !!order.isExchange;
        const color = getOrderColor(order, planningMode);
        const isUrgent = !!order.isUrgent;
        const sortKey = getOrderPriority(order, planningMode);

        // All orders, including awizacje, render as native WebGL circles.
        // Pre-advice transit stays blue; pickup_at remains a visibility filter.
        let strokeColor = isUrgent ? '#FF5B5C' : '#ffffff';
        let strokeWidth = isUrgent ? 2 : 1.5;
        if (isPlanningMode && hasPlanningTier(order)) {
          const style = getPlanningMarkerStyle(order, PLANNING_MAP_MODE.PLANNING);
          strokeColor = style.outlineColor;
          strokeWidth = style.outlineWidth;
        }

        plainFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {
            id: order.id,
            color,
            isUrgent,
            strokeColor,
            strokeWidth,
            // Tier and sortKey drive marker size, draw order and the T3 radar pulse.
            tier: (isPlanningMode && hasPlanningTier(order)) ? order.warehouseTier : -1,
            sortKey,
          },
        });

        // Return/exchange orders get a lightweight badge overlay above the circle
        if (isReturn || isExchange) {
          badges.push({
            id: order.id,
            lat,
            lng,
            icon: isExchange ? 'bx-shuffle' : 'bx-undo',
          });
        }

        // Optional planning-request count (planning view only). This is kept
        // separate from warehouseAttemptNo, which drives deadline/tier rules.
        if (
          markAttempts
          && planningPanelScreen !== 'transit'
          && isPlanningMode
          && hasPlanningTier(order)
          && getPlanningRequestCount(order) > 0
        ) {
          badges.push({
            id: `attempts-${order.id}`,
            lat,
            lng,
            planningCount: getPlanningRequestCount(order),
          });
        }
      } catch (e) { /* skip */ }
    }

    return {
      plainPointsGeoJSON: { type: 'FeatureCollection', features: plainFeatures },
      badgePoints: badges,
    };
  }, [showAllMarkers, points, selectedOrderIds, groupedMarkers, isPlanningMode, planningMode, isPlanningPointVisible, markAttempts, planningPanelScreen]);

  // Tier 2 and Tier 3 points get radar halos. The filtered source ensures only
  // currently visible points animate.
  const pulseGeoJSONByTier = useMemo(() => ({
    2: {
      type: 'FeatureCollection',
      features: plainPointsGeoJSON.features.filter((feature) => feature.properties?.tier === 2),
    },
    3: {
      type: 'FeatureCollection',
      features: plainPointsGeoJSON.features.filter((feature) => feature.properties?.tier === 3),
    },
  }), [plainPointsGeoJSON]);

  // Both pulse layers share one animation frame loop. Tier 2 uses half the visual
  // strength requested by QA: +2px marker radius, half-frequency and half-radius halo.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return undefined;

    const tiers = Object.keys(PLANNING_PULSE_CONFIG);
    const layerId = (tier) => `tier${tier}-pulse-layer`;
    const sourceId = (tier) => `tier${tier}-pulse-src`;

    if (!showAllMarkers || !isPlanningMode) {
      tiers.forEach((tier) => {
        if (map.getLayer(layerId(tier))) {
          try { map.setPaintProperty(layerId(tier), 'circle-opacity', 0); } catch (e) { /* ignore */ }
        }
      });
      return undefined;
    }

    try {
      tiers.forEach((tier) => {
        const config = PLANNING_PULSE_CONFIG[tier];
        const source = sourceId(tier);
        const layer = layerId(tier);
        const data = pulseGeoJSONByTier[tier];

        if (!map.getSource(source)) {
          map.addSource(source, { type: 'geojson', data });
        }
        if (!map.getLayer(layer)) {
          const beforeId = map.getLayer('all-points-layer') ? 'all-points-layer' : undefined;
          map.addLayer({
            id: layer,
            type: 'circle',
            source,
            paint: {
              'circle-radius': config.minRadius,
              'circle-color': config.color,
              'circle-opacity': 0.5,
              'circle-stroke-width': 0,
            },
          }, beforeId);
        }

        const mapSource = map.getSource(source);
        if (mapSource) { mapSource.setData(data); }
      });
    } catch (e) {
      return undefined;
    }

    const activeTiers = tiers.filter((tier) => pulseGeoJSONByTier[tier].features.length > 0);
    tiers.filter((tier) => !activeTiers.includes(tier)).forEach((tier) => {
      if (map.getLayer(layerId(tier))) {
        try { map.setPaintProperty(layerId(tier), 'circle-opacity', 0); } catch (e) { /* ignore */ }
      }
    });

    if (activeTiers.length === 0) {
      pulseStartedRef.current = false;
      return undefined;
    }

    pulseStartedRef.current = true;
    const step = (timestamp) => {
      if (!pulseStartedRef.current) return;
      const currentMap = mapRef.current;
      if (!currentMap) { pulseRafRef.current = null; return; }

      activeTiers.forEach((tier) => {
        const config = PLANNING_PULSE_CONFIG[tier];
        const layer = layerId(tier);
        if (!currentMap.getLayer(layer)) return;

        const phase = (timestamp % config.period) / config.period;
        const eased = 1 - (1 - phase) * (1 - phase);
        const radius = config.minRadius + (config.maxRadius - config.minRadius) * eased;
        const opacity = 0.55 * Math.sin(Math.PI * phase);
        try {
          currentMap.setPaintProperty(layer, 'circle-radius', radius);
          currentMap.setPaintProperty(layer, 'circle-opacity', opacity);
        } catch (e) { /* style reload mid-frame */ }
      });

      pulseRafRef.current = requestAnimationFrame(step);
    };
    pulseRafRef.current = requestAnimationFrame(step);

    return () => {
      pulseStartedRef.current = false;
      if (pulseRafRef.current) { cancelAnimationFrame(pulseRafRef.current); pulseRafRef.current = null; }
    };
  }, [pulseGeoJSONByTier, isMapReady, showAllMarkers, isPlanningMode]);

  // Space toggles the transient "show everything" peek while the map (or one of
  // its elements) is focused and we are in show-all planning mode. Lets the
  // planner snap between their chosen scale and the full picture without losing
  // the slider position. MapLibre's own keyboard nav uses +/-/arrows, never
  // Space, so there is no conflict.
  useEffect(() => {
    if (!showAllMarkers || !isPlanningMode) return undefined;
    const onKey = (e) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      const t = e.target;
      // Skip controls where Space has a real native action (typing a space,
      // activating a button/link). The visibility RANGE slider is intentionally
      // NOT skipped: Space does nothing native on a range input (it uses arrows),
      // so the peek shortcut must still work while the slider has focus.
      if (t && t.closest) {
        const range = t.closest('input[type="range"]');
        const native = t.closest('textarea,select,button,a,[contenteditable]')
          || (t.closest('input') && !range);
        if (native) return;
      }
      const root = containerRef.current;
      const active = document.activeElement;
      const inMap = root && ((active && root.contains(active)) || (t && root.contains(t)));
      if (!inMap) return;
      e.preventDefault();
      setShowAllOverride((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAllMarkers, isPlanningMode]);

  // Focus the map container when entering show-all so Space works immediately,
  // and drop the transient peek whenever we leave show-all.
  useEffect(() => {
    if (showAllMarkers) {
      try { containerRef.current?.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
    } else {
      setShowAllOverride(false);
    }
  }, [showAllMarkers]);

  // GeoJSON for approach lines: thin connecting lines from road marker to building
  const approachLinesGeoJSON = useMemo(() => {
    const features = [];
    if (!roadSnappedPositions || !filteredRouteOrders) {
      return { type: 'FeatureCollection', features };
    }
    filteredRouteOrders.forEach(({ order }) => {
      if (!order || !getOrderGeo(order)) return;
      const roadPos = roadSnappedPositions[order.id];
      if (!roadPos) return;
      try {
        const building = getLatLng(order);
        // Only draw if road position differs noticeably from building
        const dist = Math.abs(roadPos.lat - building.lat) + Math.abs(roadPos.lng - building.lng);
        if (dist > 0.0001) {
          features.push({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [roadPos.lng, roadPos.lat],
                [building.lng, building.lat],
              ],
            },
            properties: {},
          });
        }
      } catch (e) { /* skip */ }
    });
    return { type: 'FeatureCollection', features };
  }, [roadSnappedPositions, filteredRouteOrders]);

  // Compute start/end positions from polylines (snapped to first/last polyline point)
  const startPos = useMemo(() => {
    const sorted = [...routePolylines].sort((a, b) => a.stageIndex - b.stageIndex);
    const pos = sorted[0]?.positions?.[0];
    if (pos) return pos;
    if (startPointLocation?.lat && startPointLocation?.lng) return [startPointLocation.lat, startPointLocation.lng];
    return null;
  }, [routePolylines, startPointLocation]);

  const endPos = useMemo(() => {
    const sorted = [...routePolylines].sort((a, b) => a.stageIndex - b.stageIndex);
    const last = sorted[sorted.length - 1]?.positions;
    const pos = last && last.length > 0 ? last[last.length - 1] : null;
    if (pos) return pos;
    if (endPointLocation?.lat && endPointLocation?.lng) return [endPointLocation.lat, endPointLocation.lng];
    return null;
  }, [routePolylines, endPointLocation]);

  return (
    <div
      className="map"
      ref={containerRef}
      tabIndex={0}
      onMouseLeave={handleMapMouseLeave}
      onMouseMoveCapture={handleMapMouseMoveCapture}
      onContextMenuCapture={handleMapContextMenuCapture}
    >
      <div className="map__content" style={{ position: 'relative', width: '100%', height: '100%' }}>
        {isLoading && <div className="map__loader">Loading...</div>}

        {showAllMarkers && (
          <div className="map__planning-controls">
            <div className="map__planning-bar">
              <PlanningModeToggle mode={planningMode} onChange={handlePlanningModeChange} />
              {isPlanningMode && (
                <PlanningVisibilitySlider
                  stepIndex={effectiveIndex}
                  stepCount={visibilitySteps.length}
                  trackWidth={sliderWidth}
                  onChange={handleStepIndexChange}
                  tierTicks={tierTicks}
                  onTierStepClick={handleTierStepClick}
                  markAttempts={markAttempts}
                  onMarkAttemptsChange={handleMarkAttemptsChange}
                  screen={planningPanelScreen}
                  onScreenChange={handlePlanningPanelScreenChange}
                  transitDays={transitVisibilityDays}
                  onTransitDaysChange={handleTransitVisibilityDaysChange}
                />
              )}
            </div>
          </div>
        )}

        <MapGL
          initialViewState={{
            latitude: defaultCenter[0],
            longitude: defaultCenter[1],
            zoom: DEFAULT_ZOOM,
          }}
          style={{ height: '100%', width: '100%' }}
          mapStyle={MAP_STYLE}
          onLoad={handleMapLoad}
          onMoveEnd={handleMoveEnd}
          onClick={handleMapClick}
          scrollZoom={true}
          // Disable native double-click zoom: the planner rapidly clicks
          // start/end flag markers to detach/attach them (two clicks within
          // MapLibre's dblclick threshold), which previously bubbled to the
          // canvas and triggered an auto-zoom. Also disabled because the
          // feature itself is unwanted in this UI — zooming is done via the
          // +/- controls and the mouse wheel only. (Mateusz's feedback,
          // 2026-04-21: double-clicking the map zooms in and should be removed.)
          doubleClickZoom={false}
          // Map rotation/tilt is deliberately disabled (ticket 869efmumy):
          // planners rotated the map accidentally while right-clicking points
          // (right-click opens the info window, and a slight mouse move during
          // the click triggered MapLibre's drag-rotate). There is no compass
          // control to reset bearing, so a rotated map stayed rotated. The
          // remaining touch/keyboard rotation gestures are disabled in
          // handleMapLoad via disableRotation().
          dragRotate={false}
          touchPitch={false}
          maxPitch={0}
          attributionControl={true}
          interactiveLayerIds={showAllMarkers ? POINT_LAYER_IDS : []}
        >
          {/* Custom controls */}
          <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 25000, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <button
              type="button"
              onClick={handleZoomIn}
              style={{
                width: 30, height: 30, cursor: 'pointer', background: '#fff', border: '1px solid #ccc',
                borderRadius: '4px 4px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, lineHeight: 1,
              }}
              title="Przybliż"
            >
              +
            </button>
            <button
              type="button"
              onClick={handleZoomOut}
              style={{
                width: 30, height: 30, cursor: 'pointer', background: '#fff', border: '1px solid #ccc',
                borderRadius: '0 0 4px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, lineHeight: 1, marginTop: -1,
              }}
              title="Oddal"
            >
              −
            </button>
            <button
              type="button"
              onClick={handleFullscreen}
              style={{
                width: 30, height: 30, cursor: 'pointer', background: '#fff', border: '1px solid #ccc',
                borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 4,
              }}
              title="Pełny ekran"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="#333">
                <path d="M1 1h5v2H3v3H1V1zm11 0h5v5h-2V3h-3V1zM1 12h2v3h3v2H1v-5zm14 3h-3v2h5v-5h-2v3z" />
              </svg>
            </button>
          </div>

          {/* Route polylines */}
          {routePolylines &&
            routePolylines.map(
              (polyline) =>
                polyline.positions &&
                polyline.positions.length > 0 && (
                  <Source
                    key={`route-${polyline.stageIndex}`}
                    id={`route-${polyline.stageIndex}`}
                    type="geojson"
                    data={{
                      type: 'Feature',
                      properties: {},
                      geometry: {
                        type: 'LineString',
                        coordinates: polyline.positions.map(([lat, lng]) => [lng, lat]),
                      },
                    }}
                  >
                    <Layer
                      id={`route-line-${polyline.stageIndex}`}
                      type="line"
                      paint={{
                        ...ROUTE_LINE_PAINT,
                        'line-color': polyline.color || '#2563eb',
                      }}
                    />
                  </Source>
                )
            )}

          {/* Approach lines: thin dotted lines from road to delivery marker */}
          {approachLinesGeoJSON.features.length > 0 && (
            <Source id="approach-lines" type="geojson" data={approachLinesGeoJSON}>
              <Layer
                id="approach-lines-layer"
                type="line"
                paint={{
                  'line-color': '#2563eb',
                  'line-width': 2,
                  'line-opacity': 0.5,
                  'line-dasharray': [4, 3],
                }}
              />
            </Source>
          )}

          {/* Grouped markers (multiple orders at same location) */}
          {groupedMarkers &&
            Object.entries(groupedMarkers).map(([coordKey, group]) => {
              if (group && group.coordinates && group.coordinates.length === 2) {
                const groupOrders = group.markers.map(m => m.properties?.order).filter(Boolean);
                // In planning mode (collapsed group) the bubble reflects only the
                // members that pass the current filter: hidden when none pass, and
                // BOTH the count and the color come from the visible subset — a
                // "384" bubble while the slider shows 2 urgent members was lying,
                // and a red group whose red members are filtered out was too.
                // Expanding a group still reveals all members (explicit action).
                // Use selectedOrderIds (the route-order source of truth) for the
                // selected bypass, NOT the per-marker `o.selected` flag: an
                // on-route order also arrives via `points` without that flag, and
                // the route copy that carries it is de-duped away (see
                // groupedMarkers). Relying on `o.selected` could let the transit
                // filter / slider cutoff hide a group that actually holds a
                // selected order. isPlanningPointVisible already bypasses both
                // when isSelected is true.
                const visibleGroupOrders = (isPlanningMode && !expandedGroups[coordKey])
                  ? groupOrders.filter(o => isPlanningPointVisible(o, selectedOrderIds.has(o.id)))
                  : groupOrders;
                if (isPlanningMode && !expandedGroups[coordKey] && visibleGroupOrders.length === 0) {
                  return null;
                }
                const [longitude, latitude] = group.coordinates;
                const transitOnly = visibleGroupOrders.length > 0
                  && visibleGroupOrders.every(o => (getDaysUntilPickupAt(o) ?? -1) >= 0);
                const groupColor = isPlanningMode
                  ? getGroupColor(visibleGroupOrders, transitOnly ? TRANSIT_FILL_COLOR : undefined)
                  : undefined;
                const groupPriority = Math.max(...visibleGroupOrders.map(o => getOrderPriority(o, planningMode)), 0);
                const groupSizeBoost = isPlanningMode ? getGroupSizeBoost(visibleGroupOrders) : 0;
                return (
                  <MapMarker key={`group-${coordKey}`} longitude={longitude} latitude={latitude} anchor="center"
                    style={{ willChange: 'auto', zIndex: expandedGroups[coordKey] ? 9000 : 100 + groupPriority }}>
                    <GroupedMarker
                      group={group}
                      coordKey={coordKey}
                      isExpanded={!!expandedGroups[coordKey]}
                      onToggle={handleGroupToggle}
                      color={groupColor}
                      count={visibleGroupOrders.length}
                      sizeBoost={groupSizeBoost}
                    />
                  </MapMarker>
                );
              }
              return null;
            })}

          {/* Expanded group markers — individual orders placed in a circle */}
          {mapRef.current && Object.entries(expandedGroups).map(([coordKey, isExpanded]) => {
            if (!isExpanded) return null;
            const group = groupedMarkers[coordKey];
            if (!group || !mapRef.current) return null;
            const [cLng, cLat] = group.coordinates;
            const centerPx = mapRef.current.project([cLng, cLat]);
            const radius = BASE_RADIUS + group.markers.length * 4;

            return group.markers
              .slice()
              .sort((a, b) => a.properties.order.id - b.properties.order.id)
              .map((marker, index) => {
                const angle = (index / group.markers.length) * 2 * Math.PI;
                const adjustedAngle = angle - Math.PI / 2;
                const px = centerPx.x + radius * Math.cos(adjustedAngle);
                const py = centerPx.y + radius * Math.sin(adjustedAngle);
                const lngLat = mapRef.current.unproject([px, py]);
                const order = marker.properties?.order || marker;

                return (
                  <MapMarker key={`expanded-${coordKey}-${index}`}
                    longitude={lngLat.lng} latitude={lngLat.lat} anchor="center"
                    style={{ willChange: 'auto', zIndex: 100 + getOrderPriority(order, planningMode) }}>
                    <div
                      onPointerDownCapture={stopMarkerMapEvent}
                      onMouseDownCapture={stopMarkerMapEvent}
                      onMouseUpCapture={stopMarkerMapEvent}
                      onClickCapture={(e) => {
                        stopMarkerMapEvent(e);
                        const isSelected = filteredRouteOrders.some(ro => ro.order.id === order.id);
                        isSelected ? onUnselectOrder(order) : onSelectOrder(order);
                      }}
                      onContextMenuCapture={(e) => { stopMarkerMapEvent(e); setInfoOrder(order); }}
                    >
                      <Marker
                        order={order}
                        routeOrders={filteredRouteOrders}
                        routeId={routeId}
                        planningColor={isPlanningMode
                          ? (getPlanningTierColor(order, null) || ((getDaysUntilPickupAt(order) ?? -1) >= 0 ? TRANSIT_FILL_COLOR : null))
                          : null}
                      />
                    </div>
                  </MapMarker>
                );
              });
          })}

          {/* Plain ungrouped points — native WebGL circles for performance */}
          {showAllMarkers && (
            <Source id="all-points" type="geojson" data={plainPointsGeoJSON}>
              <Layer
                id="all-points-layer"
                type="circle"
                layout={{
                  // Higher-priority points draw on top within the layer (T3/urgent above greens).
                  'circle-sort-key': ['coalesce', ['get', 'sortKey'], 0],
                }}
                paint={{
                  // Tier 2 gets half of the Tier 3 size boost.
                  'circle-radius': ['match', ['get', 'tier'],
                    3, PLANNING_MARKER_RADII[3],
                    2, PLANNING_MARKER_RADII[2],
                    PLANNING_MARKER_RADII.default,
                  ],
                  'circle-color': ['get', 'color'],
                  'circle-stroke-width': ['coalesce', ['get', 'strokeWidth'], 1.5],
                  'circle-stroke-color': ['get', 'strokeColor'],
                }}
              />
            </Source>
          )}


          {/* Badge overlays — return/exchange icons and planning request count. */}
          {showAllMarkers && badgePoints.map(({ id, lat, lng, icon, planningCount }) => (
            <MapMarker key={`badge-${id}`} longitude={lng} latitude={lat} anchor="bottom" style={{ willChange: 'auto', pointerEvents: 'none', zIndex: 9500 }}>
              {planningCount != null ? (
                <span className="map__marker-attempts" title={`Zaplanowano: ${planningCount}`}>
                  {planningCount}
                </span>
              ) : (
                <div className="map__marker-badge" style={{ position: 'relative', top: 0, left: 0, transform: 'none' }}>
                  <i className={`bx ${icon}`} />
                </div>
              )}
            </MapMarker>
          ))}

          {/* Start point marker */}
          {startPos && (
            <MapMarker key="start-point" longitude={startPos[1]} latitude={startPos[0]} anchor="center" style={{ willChange: 'auto' }}>
              <StartPoint />
            </MapMarker>
          )}

          {/* End point marker */}
          {endPos && (
            <MapMarker key="end-point" longitude={endPos[1]} latitude={endPos[0]} anchor="center" style={{ willChange: 'auto' }}>
              <EndPoint />
            </MapMarker>
          )}

          {/* Route orders markers (selected orders on route) */}
          {filteredRouteOrders &&
            filteredRouteOrders.map(({ order }) => {
              if (order && getOrderGeo(order)) {
                try {
                  // Prefer road-snapped position (on the road) for planner readability
                  const roadSnapped = roadSnappedPositions && roadSnappedPositions[order.id];
                  const snapped = snappedPositions && snappedPositions[order.id];
                  const geocoded = getLatLng(order);
                  const lat = roadSnapped ? roadSnapped.lat : (snapped ? snapped.lat : geocoded.lat);
                  const lng = roadSnapped ? roadSnapped.lng : (snapped ? snapped.lng : geocoded.lng);
                  const coordKey = `${lat.toFixed(3)},${lng.toFixed(3)}`;

                  if (groupedMarkers && groupedMarkers[coordKey]) {
                    return null;
                  }

                  return (
                    <MapMarker key={order.trackingId} longitude={lng} latitude={lat} anchor="center" style={{ willChange: 'auto', zIndex: 9000 + getOrderPriority(order, planningMode) }}>
                      <div
                        onPointerDownCapture={stopMarkerMapEvent}
                        onMouseDownCapture={stopMarkerMapEvent}
                        onMouseUpCapture={stopMarkerMapEvent}
                        onClickCapture={(e) => { stopMarkerMapEvent(e); onUnselectOrder(order); }}
                        onContextMenuCapture={(e) => { stopMarkerMapEvent(e); setInfoOrder(order); }}
                      >
                        <Marker
                          order={{ ...order, selected: true }}
                          routeOrders={filteredRouteOrders}
                          routeId={routeId}
                        />
                      </div>
                    </MapMarker>
                  );
                } catch (error) {
                  console.warn('Error rendering route order:', order, error);
                  return null;
                }
              }
              return null;
            })}

          {/* Native connector lines for expanded groups */}
          {connectorLinesGeoJSON.features.length > 0 && (
            <Source id="connector-lines" type="geojson" data={connectorLinesGeoJSON}>
              <Layer
                id="connector-lines-layer"
                type="line"
                paint={{
                  'line-color': '#5a8dee',
                  'line-width': 2,
                  'line-opacity': 0.6,
                  'line-dasharray': [6, 4],
                }}
              />
            </Source>
          )}

        </MapGL>

        {/* InfoWindow overlay — centered on the map */}
        <div className="map__overlay-portal" ref={overlayPortalRef}>
          <div style={{ pointerEvents: 'auto' }}>{children}</div>
        </div>

        {infoOrder && getOrderGeo(infoOrder) && (
          <div className="map__info-window-portal">
            <InfoWindow order={infoOrder} onClose={() => setInfoOrder(null)} planningMode={isPlanningMode} hasReturnPicking={hasReturnPicking} />
          </div>
        )}
      </div>
    </div>
  );
};
